'use client'
import { useParams, usePathname, useSearchParams } from 'next/navigation'
import { designGrid, mq, vw, font12_Nav } from '@/styles'
import { FC, Suspense, useMemo } from 'react'
import styled from 'styled-components'
import { MODALS } from '@/context'
import { useModal, useTranslator } from '@/hooks'
import { CLIENT_NAME, LANG } from '@/utils'
import { Link } from '@/components/base/Link'
import { Figure } from '@/components/base/Figure'
import { Dropdown } from '@/components/base/dropdown'

const mediaLogo = {
  type: 'svg' as const,
  src: '/images/svg/logo--portella.svg',
  alt: `${CLIENT_NAME} logotype`,
  size: { width: 623, height: 89 }
}

const langHomepageOptions = [
  { value: 'EN', path: '/en' },
  { value: 'ES', path: '/es' },
  { value: 'FR', path: '/fr' },
  { value: 'DE', path: '/de' },
]

const langDiningOptions = [
  { value: 'EN', path: '/en/i/dining' },
  { value: 'ES', path: '/es/i/dining' },
  { value: 'FR', path: '/fr/i/dining' },
  { value: 'DE', path: '/de/i/dining' }
]

const langWellnessOptions = [
  { value: 'EN', path: '/en/i/wellness' },
  { value: 'ES', path: '/es/i/wellness' },
  { value: 'FR', path: '/fr/i/wellness' },
  { value: 'DE', path: '/de/i/wellness' }
]

const langTheHouseOptions = [
  { value: 'EN', path: '/en/i/the-house' },
  { value: 'ES', path: '/es/i/the-house' },
  { value: 'FR', path: '/fr/i/the-house' },
  { value: 'DE', path: '/de/i/the-house' }
]

const langSuitesOptions = [
  { value: 'EN', path: '/en/suites' },
  { value: 'ES', path: '/es/suites' },
  { value: 'FR', path: '/fr/suites' },
  { value: 'DE', path: '/de/suites' }
]

const getOptions = (pathname) => {
  if(pathname.includes('/dining')) return langDiningOptions
  if(pathname.includes('/wellness')) return langWellnessOptions
  if(pathname.includes('/the-house')) return langTheHouseOptions
  if (pathname.includes('/suite')) return langSuitesOptions
  return langHomepageOptions
}

export const getLinks = (links, onClick = null) => links.map((item, idx) => {
  return <li key={idx}>
    <Link href={`${item.href}`} {...onClick && { onClick }}>{item.name}</Link>
  </li>
})

export const LinkToReservation = ({ text }:{ text: string }) => {
  const { lang } = useParams()
  const searchParams = useSearchParams()
  const roomId = searchParams.get('roomId')

  return (
    <a href={`https://reservations.portellapalma.com/${lang}/hotel/portellapalma${roomId ? `/room?roomId=${roomId}` : ''}`} rel='noopener noreferrer' target='_blank'>{text}</a>
  )
}

export const Navbar:FC = () => {
  const { t } = useTranslator()
  const { lang } = useParams()
  const pathname = usePathname()
  const { toggle } = useModal(MODALS.MENU)
  const Options = useMemo(() => getOptions(pathname), [pathname])
  const Items = useMemo(() => getLinks(t('project.nav.links')), [t])

  return (
    <Nav>
      <div>
        <ul className='mobile-only'><li><button onClick={toggle}>MENU</button></li></ul>
				<ul className='desktop-only'>
					{Items}
				</ul>
        <ul className='logo_container'>
          <li>
            <Link href={'/'}>
              <Figure className='logo' media={mediaLogo} fill={false} />
            </Link>
          </li>
        </ul>
        <ul>
          <li>
            <Dropdown key={lang + pathname} className='detail-dropdown' trigger={(lang && typeof lang === 'string') ? lang?.toUpperCase() + '' : LANG.toUpperCase()} options={Options}  direction='down'/>
          </li>
					<li key='book' className='book'>
						<Suspense fallback={<>...</>}>
							<LinkToReservation text={t('project.nav.book') as string}/>
						</Suspense>
					</li>
        </ul>
      </div>
    </Nav>
  )
}

const Nav = styled.nav`
  ${designGrid({})}
  background-color: var(--color-background);
  flex-shrink: 0;
  height: 60px;
  left: 0;
  padding: 0 ${vw(10, 'mobile')};
  position: sticky;
  top: 0;
  width: 100vw;
  z-index: 1000;

  ${mq.greaterThan('tablet')} {
    padding: 0;
    height: ${vw(70,'desktop')};
  }

  ${mq.greaterThan('desktop')} {
    height:70px;
  }

	.mobile-only {
		${mq.greaterThan('tablet')} {
			display: none;
		}
	}

	.desktop-only {
		${mq.lessThan('tablet')} {
			display: none;
		}
	}

  > div {
    align-items: center;
    display: flex;
    flex-direction: row;
    gap: ${vw(30, 'mobile')};
    grid-column: 1 / span 6;
    justify-content: space-between;

    ${mq.greaterThan('tablet')} {
      gap: ${vw(30, 'desktop')};
      grid-column: 1 / span 12;
    }

    ${mq.greaterThan('desktop')} {
      gap: 30px;
    }

    .logo_container {
      position: absolute;
      left: 50%;
      transform: translateX(-50%);
    }

    > ul {
      align-items: center;
      display: flex;
      flex-direction: row;
      gap: ${vw(30, 'mobile')};
      /* width: calc(100% / 3); */
      height: 100%;

      ${mq.greaterThan('tablet')} {
        gap: ${vw(30, 'desktop')};
      }

      &:first-of-type {
        > li {
          &:first-of-type {
            display: flex;

            ${mq.greaterThan('tablet')} {
              display: none;
            }
          }
        }
      }

      &:last-of-type {
        justify-content: flex-end;
        align-items: center;
      }

      > li {
        display: flex;

        &.book {
					display: none;

          a {
            background-color: var(--color-text);
            border: 1px solid var(--color-text);
            color: var(--color-background);
            padding: 15px 30px;
            transition: 300ms background-color linear, 300ms color linear;

            ${mq.greaterThan('tablet')} {
              padding: ${vw(15, 'desktop')} ${vw(30, 'desktop')};
            }

            ${mq.greaterThan('desktop')} {
              padding: 15px 30px;
            }

            &:hover {
              border: 1px solid #000;
              background-color: #000;
              color: var(--color-background);
            }
          }
        }

        a,
        button {
          font-size: 12px;
          text-transform: uppercase;

          ${mq.greaterThan('tablet')} {
            ${font12_Nav(false, 400)}
          }

          ${mq.greaterThan('desktop')} {
            font-size: 12px;
          }
        }
      }

      &:nth-of-type(2) {
        justify-content: center;
      }

      &:last-of-type {
        li:nth-last-child(2) {
          ${mq.greaterThan('tablet')} {
            margin-left: auto;
          }
        }
      }
    }
  }

  .logo,
  p {
    grid-column: 1 / span 6;

    ${mq.greaterThan('tablet')} {
      grid-column: 3 / span 8;
    }
  }

  .logo {
    max-width: 161px;
    width: 161px;

    ${mq.greaterThan('tablet')} {
      max-width: ${vw(189, 'desktop')};
      width: 100%;
    }

    ${mq.greaterThan('desktop')} {
      max-width: 189px;
    }

  }

`



// tipos a més de 1440 fixes en ppi es 12px.
